import React from "react";
import { MapPin, Phone, Clock, Mail, Facebook, Instagram, Twitter } from "lucide-react";

const LOGO_URL = "/logo-transparent.png";

const HOURS = [
  { day: "Monday – Sunday", time: "Open 24 Hours" },
];

const NAV_LINKS = [
  { label: "Services", href: "#services" },
  { label: "About Us", href: "#about" },
  { label: "Gallery", href: "#gallery" },
  { label: "Book Now", href: "#booking" },
];

const SOCIALS = [
  { Icon: Facebook, name: "facebook" },
  { Icon: Instagram, name: "instagram" },
  { Icon: Twitter, name: "twitter" },
];

export default function Footer() {
  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer id="contact" className="bg-[#1a2744] text-white pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-14">
          {/* Logo & Tagline */}
          <div className="lg:col-span-1">
            <img
              src={LOGO_URL}
              alt="Liv's Car Wash"
              className="h-24 w-auto mb-4 brightness-110"
              data-testid="footer-logo"
            />
            <p className="text-white/55 text-sm leading-relaxed">
              Your local self-service car wash. Professional equipment, eco-friendly products, open 24/7 every day.
            </p>
          </div>

          {/* Contact */}
          <div>
            <h4
              className="font-bold text-white mb-5 text-lg"
              style={{ fontFamily: "Fredoka, sans-serif" }}
            >
              Contact Us
            </h4>
            <div className="space-y-4">
              <a
                href="tel:2266263220"
                className="flex items-center gap-3 text-white/60 hover:text-[#a8d8ea] transition-colors group"
                data-testid="contact-phone-link"
              >
                <div className="w-8 h-8 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#a8d8ea] group-hover:text-[#1a2744] transition-colors">
                  <Phone size={14} strokeWidth={2.5} />
                </div>
                <span className="text-sm font-medium">519-365-2335</span>
              </a>
              <a
                href="mailto:info@livscarwash.ca"
                className="flex items-center gap-3 text-white/60 hover:text-[#a8d8ea] transition-colors group"
                data-testid="contact-email-link"
              >
                <div className="w-8 h-8 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#a8d8ea] group-hover:text-[#1a2744] transition-colors">
                  <Mail size={14} strokeWidth={2.5} />
                </div>
                <span className="text-sm font-medium">info@livscarwash.ca</span>
              </a>
              <div
                className="flex items-start gap-3 text-white/60"
                data-testid="contact-address"
              >
                <div className="w-8 h-8 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <MapPin size={14} strokeWidth={2.5} />
                </div>
                <span className="text-sm font-medium leading-snug">
                  156 Riverview Dr
                  <br />
                  Chatham, ON N7M 5Z8
                </span>
              </div>
            </div>
          </div>

          {/* Hours */}
          <div>
            <h4
              className="font-bold text-white mb-5 text-lg"
              style={{ fontFamily: "Fredoka, sans-serif" }}
            >
              Hours
            </h4>
            <div className="space-y-4">
              {HOURS.map((h) => (
                <div
                  key={h.day}
                  className="border-b border-white/10 pb-3 last:border-0"
                  data-testid={`footer-hours-${h.day.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="text-sm font-semibold text-white/80">{h.day}</div>
                  <div className="text-sm text-white/45 mt-0.5">{h.time}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4
              className="font-bold text-white mb-5 text-lg"
              style={{ fontFamily: "Fredoka, sans-serif" }}
            >
              Quick Links
            </h4>
            <ul className="space-y-3">
              {NAV_LINKS.map((link) => (
                <li key={link.label}>
                  <button
                    onClick={() => scrollTo(link.href)}
                    className="text-white/55 hover:text-[#a8d8ea] transition-colors text-sm font-medium"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-white/35 text-sm">
            © {new Date().getFullYear()} Liv's Car Wash. All rights reserved.
          </p>
          <div className="flex items-center gap-3">
            {SOCIALS.map(({ Icon, name }) => (
              <a
                key={name}
                href="#"
                aria-label={name}
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#a8d8ea] hover:text-[#1a2744] transition-all duration-200 hover:-translate-y-0.5"
                data-testid={`footer-social-${name}`}
              >
                <Icon size={15} strokeWidth={2.5} />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
